interface Card {
  category?: string
  featured?: boolean
  title: string
  description?: string
  href: string
}

export interface SingleFAQ {
  title: string
  description: string
  cards: Omit<Card, `category`>[]
}

export interface GroupOfFAQs {
  title: string
  faqs: SingleFAQ[]
}

export interface TitleWithFAQs {
  type: `TitleWithFAQs`
  title: string
  slug?: string
  groups: GroupOfFAQs[]
}

export interface TitleWithCards {
  type: `TitleWithCards`
  slug?: string
  title: string
  cards: Omit<Card, `featured`>[]
  cta?: {
    title: string
    href: string
  }
}

export interface SingleCard {
  type: `SingleCard`
  slug?: string
  title: string
  description: string
  href: string
  cta: {
    title: string
  }
}

const productFeatures: TitleWithFAQs = {
  type: `TitleWithFAQs`,
  title: `Product Features`,
  slug: `product-features`,
  groups: [
    {
      title: `Data Collection`,
      faqs: [
        {
          title: `Data Collection Sources`,
          description: `Collect data from all of your data sources`,
          cards: [
            {
              title: `Web`,
              description: `Collect data from web sources`,
              href: `https://segment.com/docs/connections/sources/#website-libraries`,
            },
            {
              featured: true,
              title: `Analytics.js 2.0`,
              description: `The most performant way to collect data from web`,
              href: `https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/`,
            },
            {
              title: `Mobile`,
              description: `Collect data from mobile properties like Android and iOS`,
              href: `https://segment.com/docs/connections/sources/#mobile`,
            },
            {
              title: `Swift Mobile Library`,
              description: `Collect data from iOS apps, including tvOS, watchOS, macOS, and Linux`,
              href: `https://segment.com/docs/connections/sources/catalog/libraries/mobile/swift-ios/`,
            },
            {
              featured: true,
              title: `Kotlin Mobile Library`,
              description: `Collect data from Android apps`,
              href: `https://segment.com/docs/connections/sources/catalog/libraries/mobile/kotlin-android/`,
            },
            {
              title: `Server`,
              description: `Collect data from server-side sources like Node, Python, or Ruby`,
              href: `https://segment.com/docs/connections/sources/catalog/#server`,
            },
            {
              title: `Cloud`,
              description: `Collect data from cloud tools like Twilio, Salesforce, and more`,
              href: `https://segment.com/docs/connections/sources/catalog/#cloud-apps`,
            },
          ],
        },
        {
          title: `Data Collection Tooling & Observability`,
          description: `Use features that enable you to test and debug your analytics implementation`,
          cards: [
            {
              title: `Debugger`,
              description: `Confirm that API calls made from your website, mobile app, or servers are being successfully collected`,
              href: `https://segment.com/docs/connections/sources/debugger`,
            },
            {
              title: `Event Delivery`,
              description: `Easily evaluate and diagnose issues related to data deliverability`,
              href: `https://segment.com/docs/connections/event-delivery`,
            },
            {
              title: `Typewriter`,
              description: `Automatically align analytics tracking code to a pre-defined Tracking Plan spec`,
              href: `https://segment.com/docs/protocols/apis-and-extensions/typewriter`,
            },
          ],
        },
        {
          title: `Customize Your Data Collection`,
          description: `Access tools that allow you to customize how your data is collected`,
          cards: [
            {
              title: `Source Functions`,
              description: `Collect and transform any stream of customer data.`,
              href: `https://segment.com/docs/connections/functions/source-functions/`,
            },
            {
              title: `Batching for Analytics.js 2.0`,
              description: `Improve performance by batching events to Segment`,
              href: `https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#batching`,
            },
          ],
        },
      ],
    },
    {
      title: `Data Integration`,
      faqs: [
        {
          title: `Data Integrations`,
          description: `Easily enable and send data to the tools you need`,
          cards: [
            {
              title: `Destination Catalog`,
              description: `${process.env.INTEGRATION_COUNT}+ pre-built integrations for the leading SaaS tools`,
              href: `https://segment.com/docs/connections/destinations/catalog/`,
            },
            {
              featured: true,
              title: `Destination Actions`,
              description: `Configure specific destinations to match your use cases and data needs`,
              href: `https://segment.com/docs/connections/destinations/actions/`,
            },
            {
              title: `Data Warehouses & Lakes`,
              description: `Send data to a data warehouse or data lake in S3`,
              href: `https://segment.com/docs/connections/storage/data-lakes/`,
            },
          ],
        },
        {
          title: `Customize Your Integrations`,
          description: `Access tools that allow you to customize how your data is collected`,
          cards: [
            {
              title: `Destination Functions`,
              description: `Connect featured tools to Segment or adapt existing ones`,
              href: `https://segment.com/docs/connections/functions/destination-functions/`,
            },
            {
              title: `Destination Filters`,
              description: `Activate the data flowing to each of your integrations`,
              href: `https://segment.com/docs/connections/destinations/destination-filters/`,
            },
            {
              title: `Data Transformations`,
              description: `Fix bad data with data transformations`,
              href: `https://segment.com/docs/protocols/transform/`,
            },
          ],
        },
      ],
    },
    {
      title: `Profile Aggregation`,
      faqs: [
        {
          title: `Identity Resolution`,
          description: `Merge the complete history of each customer into a single profile with Twilio Engage`,
          cards: [
            {
              title: `Configurable Identity Graph`,
              description: `Design your identity graph with out-of-the-box and custom identifiers`,
              href: `https://segment.com/docs/personas/identity-resolution/identity-resolution-onboarding/`,
            },
            {
              title: `Deterministic Identity Resolution`,
              description: `Trust your customer profiles are based on accurate, comprehensive first-party data`,
              href: `https://segment.com/blog/identity-resolution/`,
            },
          ],
        },
        {
          title: `Profile Enrichment`,
          description: `Build custom profile traits using your customer data`,
          cards: [
            {
              title: `Computed Traits`,
              description: `Create dynamic user- or account-level calculations based on events and event properties`,
              href: `https://segment.com/docs/personas/computed-traits/`,
            },
            {
              title: `SQL Traits`,
              description: `Import any data from your data warehouse into Twilio Engage`,
              href: `https://segment.com/docs/personas/sql-traits/`,
            },
          ],
        },
        {
          title: `Personalization at Scale`,
          description: `Deliver customer-first experiences powered by real-time customer interactions`,
          cards: [
            {
              title: `Profile API`,
              description: `Query the entire user profile to deliver traits to any application`,
              href: `https://segment.com/docs/personas/profile-api/`,
            },
            {
              featured: true,
              title: `Journeys`,
              description: `Design personalized experiences across every connected channel`,
              href: `https://segment.com/journeys`,
            },
          ],
        },
      ],
    },
    {
      title: `Tools & Privacy`,
      faqs: [
        {
          title: `Scale your implementation`,
          description: `Scale your customer data infrastructure to support featured teams, tools, and data volumes`,
          cards: [
            {
              featured: true,
              title: `Multi-Instance Destinations`,
              description: `Connect multiple instances of a destination to a single source`,
              href: `https://segment.com/docs/connections/destinations/add-destination/#multi-instance-destinations-and-device-mode`,
            },
            {
              title: `Public API`,
              description: `Programmatic access and control for your Twilio Segment implementation`,
              href: `https://reference.segmentapis.com/`,
            },
            {
              title: `Privacy & Controls`,
              description: `Privacy tools to help your organization comply with regulations`,
              href: `https://segment.com/docs/privacy/`,
            },
          ],
        },
      ],
    },
  ],
}

const card: SingleCard = {
  type: `SingleCard`,
  title: `Coming soon: Developer Center 2.0`,
  description: `Build your own custom integrations on the Segment platform. Sign up to get early access to the beta.`,
  href: `https://airtable.com/shrvZzQ6NTTwsc6rQ`,
  cta: {
    title: `Sign up for Beta`,
  },
}

const engineeringBlog: TitleWithCards = {
  type: `TitleWithCards`,
  title: `Engineering Blog`,
  slug: `engineering-blog`,
  cards: [
    {
      category: `Blog post`,
      title: `The million dollar engineering problem`,
      href: `https://segment.com/blog/the-million-dollar-eng-problem/`,
    },
    {
      category: `Blog post`,
      title: `Goodbye Microservices: From 100s of problem children to 1 superstar`,
      href: `https://segment.com/blog/goodbye-microservices/`,
    },
    {
      category: `Blog post`,
      title: `Highlighting Performance Improvements Across Our Most Popular SDKs`,
      href: `https://segment.com/blog/sdk-performance-improvements/`,
    },
    {
      category: `Blog post`,
      title: `Future-proof your customer data infrastructure with Destination Actions`,
      href: `https://segment.com/blog/introducing-destination-actions/`,
    },
  ],
  cta: {
    title: `Read more blog posts`,
    href: `https://segment.com/blog/engineering/`,
  },
}

const recipesGuides: TitleWithCards = {
  type: `TitleWithCards`,
  title: `Recipes & Guides`,
  slug: `recipes-guides`,
  cards: [
    {
      category: `Recipe`,
      title: `Powering Your Customer Engagement Strategy Using Online & Offline Data`,
      href: `/recipes/powering-your-customer-engagement-strategy-using-online-and-offline-data/`,
    },
    {
      category: `Recipe`,
      title: `Run predictive analytics with SQL commands`,
      href: `/recipes/run-predictive-analytics-with-sql-commands/`,
    },
    {
      category: `Recipe`,
      title: `Build a Hyper-Personalized Contact Center with Twilio Engage and Twilio Flex`,
      href: `/recipes/build-a-hyper-personalized-contact-center-with-segment-personas-and-twilio-flex/`,
    },
    {
      category: `Recipe`,
      title: `Track user behavior across all connected devices, including watchOS, tvOS, or iOS`,
      href: `/recipes/track-user-behavior-across-connected-devices/`,
    },
    {
      category: `Recipe`,
      title: `Build customized reports using multiple instances of Google Analytics`,
      href: `/recipes/build-customized-reports-using-google-analytics/`,
    },
  ],
  cta: {
    title: `Explore the recipe catalog`,
    href: `/recipes/`,
  },
}

const caseStudies: TitleWithCards = {
  type: `TitleWithCards`,
  title: `Case Studies`,
  slug: `case-studies`,
  cards: [
    {
      category: `Case Study`,
      title: `ClearScore`,
      description: `Data point: 3x cost savings by leveraging Segment’s platform instead of building in-house`,
      href: `/customers/clearscore/`,
    },
    {
      category: `Case Study`,
      title: `Good Eggs`,
      description: `Zero engineering time spend managing data pipelines`,
      href: `/customers/goodeggs/`,
    },
    {
      category: `Case Study`,
      title: `Halp`,
      description: `4x improvement in the number of activated sign-ups`,
      href: `/customers/halp/`,
    },
  ],
  cta: {
    title: `See more case studies`,
    href: `/customers/`,
  },
}

const openSourceTools: TitleWithCards = {
  type: `TitleWithCards`,
  title: `Open Source Tools`,
  slug: `open-source-tools`,
  cards: [
    {
      category: `Tools`,
      title: `analytics.js`,
      description: `The hassle-free way to integrate analytics into any web application.`,
      href: `https://github.com/segmentio/analytics-next`,
    },
    {
      category: `Tools`,
      title: `analytics-kotlin`,
      description: `The hassle-free way to add Segment analytics to your Kotlin app.`,
      href: `https://github.com/segmentio/analytics-kotlin`,
    },
    {
      category: `Tools`,
      title: `analytics-swift`,
      description: `The hassle-free way to add Segment analytics to your Swift app.`,
      href: `https://github.com/segmentio/analytics-swift`,
    },
    {
      category: `Tools`,
      title: `evergreen`,
      description: `🌲 Evergreen React UI Framework by Segment`,
      href: `https://github.com/segmentio/evergreen`,
    },
    {
      category: `Tools`,
      title: `kafka-go`,
      description: `Kafka library in Go`,
      href: `https://github.com/segmentio/kafka-go`,
    },
    {
      category: `Tools`,
      title: `chamber`,
      description: `CLI for managing secrets`,
      href: `https://github.com/segmentio/chamber`,
    },
  ],
  cta: {
    title: `More open source tools`,
    href: `https://github.com/segmentio`,
  },
}

const otherResources: TitleWithCards = {
  type: `TitleWithCards`,
  title: `Other Resources`,
  slug: `other-resources`,
  cards: [
    {
      title: `Segment infrastructure overview`,
      description: `Under the hood of the system that processes 1M+ events per second`,
      href: `/infrastructure/`,
    },
    {
      title: `Segment for engineering overview`,
      description: `A Segment overview specifically written for engineers`,
      href: `/engineering/?ref=nav`,
    },
    {
      title: `Release notes`,
      description: `A running list of featured products and features released`,
      href: `https://segment.com/docs/release-notes/`,
    },
    {
      title: `API status`,
      description: `A real-time look at the status of Segment’s systems and historical incident reports`,
      href: `https://status.segment.com/`,
    },
  ],
}

type DataProps = TitleWithCards | SingleCard | TitleWithFAQs

const data: DataProps[] = [
  productFeatures,
  card,
  engineeringBlog,
  recipesGuides,
  caseStudies,
  openSourceTools,
  otherResources,
]
export default data
